import { useCallback, useEffect, useMemo, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import type { AxiosInstance } from "axios";
import { type Path, useForm } from "react-hook-form";

import { toastError, toastSuccess } from "@alpacahq/ui/old";

import useGetCountries from "~/v2/api/hooks/useGetCountries";
import { useMutationRequest } from "~/v2/api/hooks/useMutationRequest";
import { queryKeys } from "~/v2/api/queryKeys";
import type { OwnerDetails } from "~/v2/api/rest/account";
import type {
  CreateWhitelistAddressArgs,
  TravelRuleInfo,
  Vasp,
} from "~/v2/api/rest/cryptoWallet";
import {
  createWhitelistedAddress,
  updateTravelRuleInfo,
} from "~/v2/api/rest/cryptoWallet";
import type {
  AssetAddressData,
  BeneficiaryType,
  WhitelistAddressMode,
} from "../types";
import { handleTravelRuleMutationError } from "./handleMutationError";
import {
  buildPayload,
  makeTravelRuleRefinement,
  TRAVEL_RULE_DEFAULTS,
  type TravelRuleFormValues,
  type TravelRuleUseFormProps,
  travelRuleSchema,
} from "./schema";
import {
  type AddressMatchModalState,
  coerceOptionValue,
  isFilled,
  type RequestedField,
  type TravelRuleRequest,
} from "./travelRuleExtraFields";
import { useVaspSearch, type VaspSearchState } from "./useVaspSearch";

export type { AddressMatchModalState };

interface UseTravelRuleFormProps {
  cryptoWalletAxiosInstance: AxiosInstance;
  accountId: string;
  assetAddressData: AssetAddressData;
  mode: WhitelistAddressMode;
  accountDetails?: OwnerDetails;
  onSuccess: () => void;
  initialValues?: Partial<TravelRuleFormValues>;
}

export const useTravelRuleForm = ({
  cryptoWalletAxiosInstance,
  accountId,
  assetAddressData,
  mode,
  accountDetails,
  onSuccess,
  initialValues,
}: UseTravelRuleFormProps) => {
  // UI-only state (never sent to the backend; drives rendering + validation branching).
  const [beneficiaryType, setBeneficiaryType] =
    useState<BeneficiaryType | null>(() => {
      if (initialValues?.beneficiary_entity_name) {
        return "business";
      }
      if (
        initialValues?.beneficiary_given_name ||
        initialValues?.beneficiary_family_name
      ) {
        return "individual";
      }
      return null;
    });

  const [ownsWallet, setOwnsWallet] = useState<boolean | null>(() => {
    if (mode.kind !== "edit") {
      return null;
    }
    const travelRuleInfo = mode.existing.travel_rule_info;
    if (travelRuleInfo?.beneficiary_is_self_hosted !== true) {
      return null;
    }
    if (
      accountDetails &&
      travelRuleInfo.beneficiary_given_name === accountDetails.given_name &&
      travelRuleInfo.beneficiary_family_name === accountDetails.family_name
    ) {
      return true;
    }
    return false;
  });

  const [isManualEntry, setIsManualEntry] = useState<boolean>(
    !!initialValues?.beneficiary_manual_entry?.vasp_name,
  );

  const [selectedVasp, setSelectedVasp] = useState<Vasp | null>(() => {
    if (
      mode.kind === "edit" &&
      mode.existing.travel_rule_info?.beneficiary_vasp_id &&
      !initialValues?.beneficiary_manual_entry?.vasp_name
    ) {
      const vaspId = mode.existing.travel_rule_info.beneficiary_vasp_id;
      return {
        id: vaspId,
        did: vaspId,
        name: mode.existing.institution_name ?? "",
      };
    }
    return null;
  });

  // True when `selectedVasp` was synthesized from a hand-entered DID rather
  // than picked from the directory, so the UI knows to ask for a display name.
  const [isDidEntry, setIsDidEntry] = useState(false);
  const [didNameTouched, setDidNameTouched] = useState(false);

  const [addressMatchModal, setAddressMatchModal] =
    useState<AddressMatchModalState | null>(null);

  const [confirmedAuthorized, setConfirmedAuthorized] = useState(false);

  // Backend-driven field request, cleared on each submit. Rendered by
  // `RequestedFieldsPanel`; ADDRESS_BELONGS_TO_VASP is routed to the modal instead.
  const [request, setRequest] = useState<TravelRuleRequest | null>(null);
  // Active ONE_OF branch in the panel; auto-set to the first branch when a request arrives.
  const [selectedBranchId, setSelectedBranchId] = useState<string | null>(null);
  const { countries } = useGetCountries();
  const queryClient = useQueryClient();

  const invalidateWhitelistCaches = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: queryKeys.crypto.whitelists(accountId),
    });
    void queryClient.invalidateQueries({
      queryKey: queryKeys.crypto.approvedWhitelists(accountId),
    });
  }, [queryClient, accountId]);

  const resolver = useMemo(
    () =>
      zodResolver(
        travelRuleSchema.superRefine(
          makeTravelRuleRefinement({ beneficiaryType, isManualEntry }),
        ),
      ),
    [beneficiaryType, isManualEntry],
  );

  const {
    control,
    setValue,
    clearErrors,
    handleSubmit,
    watch,
    reset: resetForm,
    formState: { errors, isSubmitted },
  } = useForm<TravelRuleFormValues, unknown, TravelRuleFormValues>({
    // zodResolver's input/output generics don't line up with the passthrough
    // index-signature field values, so cast the resolver through the escape
    // hatch documented for this schema.
    resolver,
    mode: "onChange",
    defaultValues: { ...TRAVEL_RULE_DEFAULTS, ...initialValues },
  } as TravelRuleUseFormProps);

  // beneficiaryType is UI-only, so Zod can't drive its required-message; only show after first submit.
  const beneficiaryTypeError =
    isSubmitted && beneficiaryType === null
      ? "Please select a recipient type"
      : undefined;

  const confirmedAuthorizedError =
    isSubmitted && !confirmedAuthorized
      ? "Please confirm you are authorized to interact with this address"
      : undefined;

  const isSelfHosted = watch("beneficiary_is_self_hosted");
  const manualVaspName = watch("beneficiary_manual_entry.vasp_name") ?? "";
  const manualVaspWebsite =
    watch("beneficiary_manual_entry.vasp_website") ?? "";

  // Derived for AddressMatchModal; null collapses the modal's name row.
  const beneficiaryGivenName = watch("beneficiary_given_name") ?? "";
  const beneficiaryFamilyName = watch("beneficiary_family_name") ?? "";
  const beneficiaryEntityName = watch("beneficiary_entity_name") ?? "";
  const walletOwnerName = useMemo<string | null>(() => {
    if (beneficiaryEntityName.trim()) {
      return beneficiaryEntityName.trim();
    }
    const personName =
      `${beneficiaryGivenName} ${beneficiaryFamilyName}`.trim();
    return personName || null;
  }, [beneficiaryGivenName, beneficiaryFamilyName, beneficiaryEntityName]);

  const needsVasp = isSelfHosted === false;

  const editModeVaspLabel = useMemo(() => {
    if (mode.kind !== "edit") {
      return "";
    }
    if (initialValues?.beneficiary_manual_entry?.vasp_name) {
      return "Manual Entry";
    }
    return mode.existing.institution_name ?? "";
  }, [mode, initialValues]);

  const vaspSearchApi = useVaspSearch({
    accountId,
    searchEnabled: needsVasp && !selectedVasp && !isManualEntry,
    initialQuery: editModeVaspLabel,
  });

  const onSelectVasp = (vasp: Vasp) => {
    setSelectedVasp(vasp);
    setIsManualEntry(false);
    setIsDidEntry(false);
    setDidNameTouched(false);
    vaspSearchApi.setQuery(vasp.name);
    setValue("beneficiary_vasp_id", vasp.did ?? vasp.id, {
      shouldValidate: true,
    });
    setValue("beneficiary_manual_entry.vasp_name", "", {
      shouldValidate: false,
    });
    setValue("beneficiary_manual_entry.vasp_website", "", {
      shouldValidate: false,
    });
    clearErrors([
      "beneficiary_manual_entry.vasp_name",
      "beneficiary_manual_entry.vasp_website",
    ]);
  };

  // Direct DID entry, for users who already know the beneficiary's VASP DID.
  // The synthesized VASP mirrors what edit mode builds from a stored record, so
  // `buildPayload` treats both the same: DID to the wire, name to
  // `institution_name`. The name starts empty and submit stays blocked until
  // it's filled, because cryptowallet rejects a nameless exchange address.
  const onSelectDidEntry = (did: string) => {
    setSelectedVasp({ id: did, did, name: "" });
    setIsManualEntry(false);
    setIsDidEntry(true);
    setDidNameTouched(false);
    vaspSearchApi.setQuery(did);
    setValue("beneficiary_vasp_id", did, { shouldValidate: true });
    setValue("beneficiary_manual_entry.vasp_name", "", {
      shouldValidate: false,
    });
    setValue("beneficiary_manual_entry.vasp_website", "", {
      shouldValidate: false,
    });
    clearErrors([
      "beneficiary_manual_entry.vasp_name",
      "beneficiary_manual_entry.vasp_website",
    ]);
  };

  const setDidEntryName = (name: string) =>
    setSelectedVasp((prev) => (prev ? { ...prev, name } : prev));

  const onSelectManualEntry = () => {
    setSelectedVasp(null);
    setIsManualEntry(true);
    setIsDidEntry(false);
    setDidNameTouched(false);
    vaspSearchApi.setQuery("Manual Entry");
    // Skip validation: `isManualEntry` hasn't reached the resolver yet, so the
    // previous vasp-id-required refinement would fire on a field that's no
    // longer required.
    setValue("beneficiary_vasp_id", "", { shouldValidate: false });
    clearErrors("beneficiary_vasp_id");
  };

  const onClearSelection = () => {
    setSelectedVasp(null);
    setIsManualEntry(false);
    setIsDidEntry(false);
    setDidNameTouched(false);
    vaspSearchApi.setQuery("");
    // Same stale-resolver concern as `onSelectManualEntry`, inverted.
    setValue("beneficiary_vasp_id", "", { shouldValidate: false });
    setValue("beneficiary_manual_entry.vasp_name", "", {
      shouldValidate: false,
    });
    setValue("beneficiary_manual_entry.vasp_website", "", {
      shouldValidate: false,
    });
    clearErrors([
      "beneficiary_manual_entry.vasp_name",
      "beneficiary_manual_entry.vasp_website",
    ]);
  };

  // `institution_name` only travels on the create request; the update endpoint
  // takes `travel_rule_info` alone and leaves the stored name untouched. So a
  // hand-entered DID needs a display name when whitelisting a new address, but
  // not when editing one — there the backend resolves the DID and answers with
  // UNKNOWN_VASP if it can't.
  const didEntryNameRequired = mode.kind !== "edit";

  // Zod can't drive this message: the name is UI-only state feeding the
  // whitelist-level `institution_name`, not a `travel_rule_info` field. Gate it
  // on blur rather than `isSubmitted` so it can actually render — `isSubmitted`
  // would never flip while the same condition keeps submit disabled.
  const isDidEntryNameMissing =
    isDidEntry && didEntryNameRequired && !selectedVasp?.name?.trim();
  const didEntryNameError =
    (didNameTouched || isSubmitted) && isDidEntryNameMissing
      ? "Custodian or exchange name is required"
      : undefined;

  const vaspSearch: VaspSearchState = {
    query: vaspSearchApi.query,
    setQuery: vaspSearchApi.setQuery,
    results: vaspSearchApi.results,
    isLoading: vaspSearchApi.isLoading,
    hasMore: vaspSearchApi.hasMore,
    isLoadingMore: vaspSearchApi.isLoadingMore,
    onLoadMore: vaspSearchApi.onLoadMore,
    selectedVasp,
    isManualEntry,
    manualVaspName,
    manualVaspWebsite,
    setManualVaspName: (v: string) =>
      setValue("beneficiary_manual_entry.vasp_name", v, {
        shouldValidate: true,
      }),
    setManualVaspWebsite: (v: string) =>
      setValue("beneficiary_manual_entry.vasp_website", v, {
        shouldValidate: true,
      }),
    onSelectVasp,
    onSelectManualEntry,
    onClearSelection,
    isDidEntry,
    didEntryNameRequired,
    didEntryName: isDidEntry ? (selectedVasp?.name ?? "") : "",
    didEntryNameError,
    onSelectDidEntry,
    setDidEntryName,
    onBlurDidEntryName: () => setDidNameTouched(true),
  };

  const onBeneficiaryTypeChange = (bt: BeneficiaryType) => {
    setBeneficiaryType(bt);
    setValue("beneficiary_is_self_hosted", null);
    setOwnsWallet(null);
    setSelectedVasp(null);
    setIsManualEntry(false);
    setIsDidEntry(false);
    setDidNameTouched(false);
    vaspSearchApi.reset();
    setValue("beneficiary_vasp_id", "");
    setValue("beneficiary_manual_entry.vasp_name", "");
    setValue("beneficiary_manual_entry.vasp_website", "");
    // Clear all name fields so the wire payload never carries both an entity
    // name and a natural-person name (cryptowallet's `reconcileNameRequirements`
    // rejects that combination with `BeneficiaryNameChoiceError`).
    setValue("beneficiary_given_name", "", { shouldValidate: false });
    setValue("beneficiary_family_name", "", { shouldValidate: false });
    setValue("beneficiary_entity_name", "", { shouldValidate: false });
    clearErrors([
      "beneficiary_given_name",
      "beneficiary_family_name",
      "beneficiary_entity_name",
    ]);
  };

  const onIsSelfHostedChange = (value: boolean) => {
    setValue("beneficiary_is_self_hosted", value, { shouldValidate: true });
    setOwnsWallet(null);
    if (value) {
      setSelectedVasp(null);
      setIsManualEntry(false);
      setIsDidEntry(false);
      setDidNameTouched(false);
      vaspSearchApi.reset();
      setValue("beneficiary_vasp_id", "");
      setValue("beneficiary_manual_entry.vasp_name", "");
      setValue("beneficiary_manual_entry.vasp_website", "");
    }
  };

  const onOwnsWalletChange = (isOwnsWallet: boolean) => {
    setOwnsWallet(isOwnsWallet);
    if (isOwnsWallet && beneficiaryType === "individual" && accountDetails) {
      setValue("beneficiary_given_name", accountDetails.given_name ?? "", {
        shouldValidate: true,
      });
      setValue("beneficiary_family_name", accountDetails.family_name ?? "", {
        shouldValidate: true,
      });
      return;
    }
    setValue("beneficiary_given_name", "", { shouldValidate: false });
    setValue("beneficiary_family_name", "", { shouldValidate: false });
  };

  const createMutation = useMutationRequest(
    async (args: CreateWhitelistAddressArgs) =>
      createWhitelistedAddress(cryptoWalletAxiosInstance, accountId, args),
    {
      onSuccess: () => {
        invalidateWhitelistCaches();
        toastSuccess("Whitelist address created successfully");
        onSuccess();
      },
      onError: (error) => handleMutationError(error),
    },
  );

  const updateMutation = useMutationRequest(
    async (args: { whitelistId: string; travelRuleInfo: TravelRuleInfo }) =>
      updateTravelRuleInfo(
        cryptoWalletAxiosInstance,
        accountId,
        args.whitelistId,
        args.travelRuleInfo,
      ),
    {
      onSuccess: () => {
        invalidateWhitelistCaches();
        toastSuccess("Travel rule information updated");
        onSuccess();
      },
      onError: (error) => handleMutationError(error),
    },
  );

  const handleMutationError = useCallback(
    (error: Error) =>
      handleTravelRuleMutationError(error, {
        setAddressMatchModal,
        setRequest,
        toastError,
      }),
    [],
  );

  const onSubmit = handleSubmit((data: TravelRuleFormValues) => {
    // The submit button stays enabled so clicking it is what reveals what's
    // missing. Zod covers the registered fields; these four are UI-only state
    // it can't see, and each has a message keyed off `isSubmitted` that renders
    // the moment this bails.
    if (
      beneficiaryType === null ||
      !confirmedAuthorized ||
      isDidEntryNameMissing ||
      hasUnfilledRequest
    ) {
      return;
    }
    setRequest(null);
    const payload = buildPayload(data, selectedVasp, isManualEntry);

    if (mode.kind === "edit") {
      updateMutation.mutate({
        whitelistId: mode.whitelistId,
        travelRuleInfo: payload.travel_rule_info,
      });
    } else {
      const base = {
        chain: assetAddressData.chain,
        asset: assetAddressData.asset,
        address: assetAddressData.address,
      };
      if (payload.address_type === "exchange") {
        createMutation.mutate({
          ...base,
          address_type: payload.address_type,
          institution_name: payload.institution_name,
          travel_rule_info: payload.travel_rule_info,
        });
      } else {
        createMutation.mutate({
          ...base,
          address_type: payload.address_type,
          travel_rule_info: payload.travel_rule_info,
        });
      }
    }
  });

  const onCancelAddressMatch = () => setAddressMatchModal(null);

  const reset = useCallback(() => {
    resetForm({ ...TRAVEL_RULE_DEFAULTS, ...initialValues });
    setBeneficiaryType(
      initialValues?.beneficiary_entity_name
        ? "business"
        : initialValues?.beneficiary_given_name ||
            initialValues?.beneficiary_family_name
          ? "individual"
          : null,
    );
    setOwnsWallet(null);
    setIsManualEntry(!!initialValues?.beneficiary_manual_entry?.vasp_name);
    setSelectedVasp(null);
    setAddressMatchModal(null);
    setConfirmedAuthorized(false);
    setRequest(null);
    setSelectedBranchId(null);
    vaspSearchApi.reset();
  }, [initialValues, resetForm, vaspSearchApi]);

  // Apply every forced value, then re-fire the submit on the next tick so RHF
  // has time to settle setValue's validation before the resolver runs again.
  const onAcceptAddressMatch = () => {
    if (!addressMatchModal) {
      return;
    }
    for (const f of addressMatchModal.forcedFields) {
      setValue(
        f.field as Path<TravelRuleFormValues>,
        coerceOptionValue(f.rawValue, f.valueType) as string | boolean,
        { shouldValidate: true },
      );
    }
    setAddressMatchModal(null);
    setTimeout(() => onSubmit(), 0);
  };

  // Default the radio to the first ONE_OF branch when a request arrives;
  // pure REQUIRED requests have no branches to pick.
  useEffect(() => {
    if (!request) {
      setSelectedBranchId(null);
      return;
    }
    const firstBranch = request.fields.find((f) => f.branchId)?.branchId;
    setSelectedBranchId(firstBranch ?? null);
  }, [request]);

  // Switching branch wipes the other branch's form values (so the resubmit can't
  // ship stale inputs) and syncs `beneficiaryType` when the picked branch carries
  // name fields — the sync keeps the main form's superRefine + conditional name
  // inputs aligned with the user's choice.
  const onSelectBranch = useCallback(
    (branchId: string) => {
      setSelectedBranchId(branchId);
      if (!request) {
        return;
      }
      let nextBeneficiaryType: BeneficiaryType | null = null;
      for (const f of request.fields) {
        if (f.branchId !== branchId) {
          if (f.branchId) {
            setValue(f.path as Path<TravelRuleFormValues>, "" as never, {
              shouldValidate: false,
            });
          }
          continue;
        }
        if (
          f.path === "beneficiary_given_name" ||
          f.path === "beneficiary_family_name"
        ) {
          nextBeneficiaryType = "individual";
        } else if (f.path === "beneficiary_entity_name") {
          nextBeneficiaryType = "business";
        }
      }
      if (nextBeneficiaryType !== null) {
        setBeneficiaryType(nextBeneficiaryType);
      }
    },
    [request, setValue],
  );

  // Flat path list watched so `hasUnfilledRequest` recomputes as the user types.
  const requestedPaths = useMemo<string[]>(
    () => Array.from(new Set((request?.fields ?? []).map((f) => f.path))),
    [request],
  );

  // Wire paths currently rendered in the panel. The presenter hides any
  // main-form input matching one of these so the panel is the single source
  // of truth for backend-requested fields.
  const panelClaimedPaths = useMemo<ReadonlySet<string>>(
    () => new Set(requestedPaths),
    [requestedPaths],
  );
  const watchedRequestedValues = watch(
    requestedPaths as Path<TravelRuleFormValues>[],
  );
  const watchedByPath = useMemo(() => {
    const out: Record<string, unknown> = {};
    requestedPaths.forEach((p, i) => {
      out[p] = watchedRequestedValues[i];
    });
    return out;
  }, [requestedPaths, watchedRequestedValues]);

  // Disable submit until every REQUIRED field and the active ONE_OF branch
  // are fully filled. Non-selected branch fields are ignored (already cleared
  // in `onSelectBranch`).
  const hasUnfilledRequest = useMemo(() => {
    if (!request || request.fields.length === 0) {
      return false;
    }
    const required: RequestedField[] = [];
    const branchFields = new Map<string, RequestedField[]>();
    for (const f of request.fields) {
      if (!f.branchId) {
        required.push(f);
        continue;
      }
      const list = branchFields.get(f.branchId) ?? [];
      list.push(f);
      branchFields.set(f.branchId, list);
    }
    if (!required.every((f) => isFilled(watchedByPath[f.path]))) {
      return true;
    }
    if (branchFields.size === 0) {
      return false;
    }
    if (!selectedBranchId) {
      return true;
    }
    const active = branchFields.get(selectedBranchId) ?? [];
    return !active.every((f) => isFilled(watchedByPath[f.path]));
  }, [request, selectedBranchId, watchedByPath]);

  const isSubmitLoading = createMutation.isPending || updateMutation.isPending;

  const requestedFieldsError =
    isSubmitted && hasUnfilledRequest
      ? "Please complete the fields above"
      : undefined;

  return {
    control,
    errors,
    beneficiaryType,
    isSelfHosted,
    ownsWallet,
    needsVasp,
    countries,
    vaspSearch,
    onBeneficiaryTypeChange,
    onIsSelfHostedChange,
    onOwnsWalletChange,
    onSubmit,
    isSubmitLoading,
    // Only the in-flight request disables the button. Everything else that
    // blocks submission is reported as a field error instead, so the form can
    // never present a dead button with no explanation.
    isSubmitDisabled: isSubmitLoading,
    beneficiaryTypeError,
    confirmedAuthorizedError,
    requestedFieldsError,
    request,
    panelClaimedPaths,
    selectedBranchId,
    onSelectBranch,
    mode,
    confirmedAuthorized,
    onConfirmedAuthorizedChange: setConfirmedAuthorized,
    addressMatchModal,
    onAcceptAddressMatch,
    onCancelAddressMatch,
    walletOwnerName,
    reset,
  };
};

export type UseTravelRuleFormReturn = ReturnType<typeof useTravelRuleForm>;
